/**
 * Live Search - Real-time product search
 * Uses Shopify Predictive Search API
 */

class LiveSearch {
  constructor(container) {
    this.container = container;
    this.input = container.querySelector('[data-live-search-input]');
    this.results = container.querySelector('[data-live-search-results]');
    this.itemsContainer = container.querySelector('[data-live-search-items]');
    this.loading = container.querySelector('[data-live-search-loading]');
    this.empty = container.querySelector('[data-live-search-empty]');

    if (!this.input || !this.results) return;

    this.debounceTimer = null;
    this.minChars = 2;
    this.isOpen = false;

    this.init();
  }

  init() {
    // Input events
    this.input.addEventListener('input', () => this.handleInput());
    this.input.addEventListener('focus', () => this.handleFocus());

    // Close on click outside
    document.addEventListener('click', (e) => {
      if (!this.container.contains(e.target)) {
        this.close();
      }
    });

    // Keyboard navigation
    this.input.addEventListener('keydown', (e) => this.handleKeydown(e));

    // Close on escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
        this.input.blur();
      }
    });
  }

  handleInput() {
    const query = this.input.value.trim();

    // Clear previous timer
    clearTimeout(this.debounceTimer);

    if (query.length < this.minChars) {
      this.close();
      return;
    }

    // Debounce search
    this.debounceTimer = setTimeout(() => {
      this.search(query);
    }, 300);
  }

  handleFocus() {
    const query = this.input.value.trim();
    if (query.length >= this.minChars && this.itemsContainer.innerHTML) {
      this.open();
    }
  }

  handleKeydown(e) {
    if (!this.isOpen) return;

    const items = this.itemsContainer.querySelectorAll('.live-search-item');
    const activeItem = this.itemsContainer.querySelector('.live-search-item--active');
    let currentIndex = Array.from(items).indexOf(activeItem);

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        currentIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
        this.setActiveItem(items, currentIndex);
        break;
      case 'ArrowUp':
        e.preventDefault();
        currentIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
        this.setActiveItem(items, currentIndex);
        break;
      case 'Enter':
        e.preventDefault();
        if (activeItem) {
          window.location.href = activeItem.href;
        }
        break;
    }
  }

  setActiveItem(items, index) {
    items.forEach(item => item.classList.remove('live-search-item--active'));
    if (items[index]) {
      items[index].classList.add('live-search-item--active');
      items[index].scrollIntoView({ block: 'nearest' });
    }
  }

  async search(query) {
    this.showLoading();
    this.open();

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6`
      );

      if (!response.ok) throw new Error('Search failed');

      const data = await response.json();
      const products = data.resources?.results?.products || [];

      this.renderResults(products, query);
    } catch (error) {
      console.error('Live search error:', error);
      this.showEmpty();
    }
  }

  renderResults(products, query) {
    this.hideLoading();

    if (products.length === 0) {
      this.showEmpty();
      return;
    }

    this.hideEmpty();

    this.itemsContainer.innerHTML = products.map(product => this.renderItem(product, query)).join('');

    // Add click handlers
    this.itemsContainer.querySelectorAll('.live-search-item').forEach(item => {
      item.addEventListener('mouseenter', () => {
        this.itemsContainer.querySelectorAll('.live-search-item').forEach(i =>
          i.classList.remove('live-search-item--active')
        );
        item.classList.add('live-search-item--active');
      });
    });
  }

  renderItem(product, query) {
    const imageUrl = product.featured_image?.url
      ? this.getSizedImageUrl(product.featured_image.url, '100x100')
      : '//cdn.shopify.com/s/files/1/0533/2089/files/placeholder-images-image_small.png';

    const highlightedTitle = this.highlightMatch(product.title, query);
    const price = this.formatMoney(product.price);

    return `
      <a href="${product.url}" class="live-search-item">
        <div class="live-search-item__image">
          <img src="${imageUrl}" alt="${this.escapeHtml(product.title)}" loading="lazy">
        </div>
        <div class="live-search-item__content">
          <h4 class="live-search-item__title">${highlightedTitle}</h4>
          <span class="live-search-item__price">${price}</span>
        </div>
      </a>
    `;
  }

  highlightMatch(text, query) {
    const regex = new RegExp(`(${this.escapeRegex(query)})`, 'gi');
    return this.escapeHtml(text).replace(regex, '<mark>$1</mark>');
  }

  open() {
    this.isOpen = true;
    this.results.hidden = false;
  }

  close() {
    this.isOpen = false;
    this.results.hidden = true;
  }

  showLoading() {
    if (this.loading) this.loading.hidden = false;
    if (this.empty) this.empty.hidden = true;
    if (this.itemsContainer) this.itemsContainer.innerHTML = '';
  }

  hideLoading() {
    if (this.loading) this.loading.hidden = true;
  }

  showEmpty() {
    this.hideLoading();
    if (this.empty) this.empty.hidden = false;
    if (this.itemsContainer) this.itemsContainer.innerHTML = '';
  }

  hideEmpty() {
    if (this.empty) this.empty.hidden = true;
  }

  formatMoney(cents) {
    const amount = cents;
    return `${amount} ZŁ`;
  }

  getSizedImageUrl(src, size) {
    if (!src) return '';
    return src.replace(/(\.[^.]+)$/, `_${size}$1`);
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }

  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
}

// Initialize all live search instances
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-live-search]').forEach(container => {
    new LiveSearch(container);
  });
});
